import React, { useState } from "react";
import { Button, CheckCircleIcon, DocIcon, DownloadIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";

import {
  CombinationRulesPrecheckResponse,
  ICombinationRule,
  useCheckAllCombinationRules,
  useDownloadCombinationRulesReport,
  usePrecheckAllCombinationRules,
} from "~/entity/CombinationRules";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";

import CheckAllPreviewModal from "./CheckAllPreviewModal";
import { exportCombinationRulesCsv } from "./exportCsv";

import s from "~/pages/Editor/EditorDirectories/EditorCombinationRules/EditorCombinationRules.less";

type Props = {
  rules?: ICombinationRule[];
  positionsMap: Map<number, string>;
};

/** Функциональный блок над таблицей: проверка, выгрузка CSV, отчёт Excel. */
const ActionButtons = ({ rules, positionsMap }: Props) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { notifyError: notifyErrorShared, notifySuccess } = useNotify();
  const { checkAll, isLoading: isCheckingAll } = useCheckAllCombinationRules();
  const { precheckAll, isLoading: isPrecheckingAll } =
    usePrecheckAllCombinationRules();
  const { download, isLoading: isDownloadingReport } =
    useDownloadCombinationRulesReport();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [precheckResult, setPrecheckResult] =
    useState<CombinationRulesPrecheckResponse>();

  const notifyError = (error: any) => {
    notifyErrorShared(error?.message || t("combinationRules.request_error"));
  };

  const resetPreview = () => {
    setIsPreviewOpen(false);
    setPrecheckResult(undefined);
  };

  const closePreview = () => {
    if (!isCheckingAll) {
      resetPreview();
    }
  };

  const handlePrecheckAll = () => {
    setPrecheckResult(undefined);
    setIsPreviewOpen(true);
    precheckAll({
      onSuccess: (data) => {
        setPrecheckResult(data);
      },
      onError: (error) => {
        notifyError(error);
        resetPreview();
      },
    });
  };

  const handleConfirmCheckAll = () => {
    checkAll({
      onSuccess: () => {
        notifySuccess(t("combinationRules.check_all_started"));
        resetPreview();
      },
      onError: notifyError,
    });
  };

  const handleExportCsv = () => {
    exportCombinationRulesCsv(t, rules ?? [], positionsMap);
  };

  const handleDownloadReport = () => {
    download({ onError: notifyError });
  };

  const buttonProps = {
    color: "secondary",
    variant: "filled",
    bold: true,
    fontSize: "12",
    size: "middle",
    radius: 30,
  } as const;

  return (
    <>
      <Flex gap={10} className={s.actions_row}>
        {hasPermission("combinationRules.rules.report") && (
          <Button
            {...buttonProps}
            icon={<DownloadIcon size={16} fill="#495a64" />}
            onClick={handleExportCsv}
          >
            {t("combinationRules.export_excel")}
          </Button>
        )}

        {hasPermission("combinationRules.rules.checkAll") && (
          <Button
            {...buttonProps}
            icon={<CheckCircleIcon size={16} fill="#495a64" />}
            onClick={handlePrecheckAll}
            loading={isPrecheckingAll || isCheckingAll}
          >
            {t("combinationRules.check_all")}
          </Button>
        )}

        {hasPermission("combinationRules.rules.report") && (
          <Button
            {...buttonProps}
            icon={<DocIcon size={16} fill="#495a64" />}
            onClick={handleDownloadReport}
            loading={isDownloadingReport}
          >
            {t("combinationRules.report")}
          </Button>
        )}
      </Flex>

      <CheckAllPreviewModal
        open={isPreviewOpen}
        result={precheckResult}
        loading={isPrecheckingAll}
        confirming={isCheckingAll}
        onCancel={closePreview}
        onConfirm={handleConfirmCheckAll}
      />
    </>
  );
};

export default ActionButtons;
